import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { FormPageLayout } from '@/components/layout/form-page-layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import type {
	CreateLimitPolicyMutation,
	CreateLimitPolicyMutationVariables
} from '@/graphql/generated';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { useEntityForm } from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { BILLING_CYCLES_LIST_QUERY } from '@/pages/pricing-policies/lookup.gql';
import { LIMIT_POLICY_CREATE_MUTATION, LIMIT_POLICIES_LIST_QUERY } from './limit-policies.gql.tsx';
import { LimitPolicyForm } from './limit-policy-form';
import {
	buildCreateLimitPolicyInput,
	generateIdempotentLimitPolicyId,
	limitPolicyFormSchema,
	type LimitPolicyFormValues
} from './limit-policy-input';

interface CloneData {
	limitPolicyId?: string;
	name?: string;
	priority?: number;
	isActive?: boolean;
	scenarioId?: string | null;
	scenarioLabel?: string | null;
	limitId?: string;
	limitLabel?: string | null;
	billingCycleId?: number;
}

interface LocationState {
	cloneData?: CloneData;
}

export const LimitPoliciesCreate = () => {
	const go = useGo();
	const location = useLocation();
	const cloneData = (location.state as LocationState | null)?.cloneData;
	const { setPageHeader, resetPageHeader } = usePageHeader();

	const form = useEntityForm<LimitPolicyFormValues>({
		mode: cloneData ? 'clone' : 'create',
		schema: limitPolicyFormSchema,
		defaultValues: {
			name: cloneData?.name ?? '',
			priority: cloneData?.priority?.toString() ?? '',
			isActive: cloneData?.isActive ?? true,
			scenarioId: cloneData?.scenarioId ?? '',
			limitId: cloneData?.limitId ?? '',
			billingCycleId: cloneData?.billingCycleId?.toString() ?? ''
		}
	});

	// Use the pre-generated limitPolicyId from clone data, or generate a new one
	const idempotentLimitPolicyId = useMemo(
		() => cloneData?.limitPolicyId ?? generateIdempotentLimitPolicyId(),
		[cloneData?.limitPolicyId]
	);

	const {
		data: billingCyclesData,
		loading: loadingBillingCycles,
		error: billingCyclesError
	} = useQuery(BILLING_CYCLES_LIST_QUERY);

	const { data: limitPoliciesData } = useQuery(LIMIT_POLICIES_LIST_QUERY, {
		variables: { first: 100 }
	});

	const lookupsLoading = loadingBillingCycles;
	const lookupError = billingCyclesError;

	const { shouldShowLoading } = useDelayedLoading(lookupsLoading, {
		delay: 300,
		showInitialLoading: false
	});

	const billingCycles = billingCyclesData?.billingCycles ?? [];

	const existingPriorities = useMemo(() => {
		const nodes = limitPoliciesData?.limitPolicies?.nodes ?? [];
		return nodes
			.filter((node: any) => node.priority != null)
			.map((node: any) => ({
				priority: node.priority,
				label: node.name
			}));
	}, [limitPoliciesData]);

	const [createLimitPolicy, { loading: isSubmitting }] = useMutation<
		CreateLimitPolicyMutation,
		CreateLimitPolicyMutationVariables
	>(LIMIT_POLICY_CREATE_MUTATION);

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	useEffect(() => {
		setPageHeader({
			title: cloneData ? 'Clone Limit Policy' : 'Create Limit Policy',
			description: cloneData
				? 'Create a new limit policy based on an existing one'
				: 'Configure a new policy to enforce limit thresholds',
			showBackButton: true,
			onBackClick: () => go({ to: '/limit-policies' })
		});

		return () => {
			resetPageHeader();
		};
	}, [setPageHeader, resetPageHeader, go, cloneData]);

	const handleSubmit = async (values: LimitPolicyFormValues) => {
		let input;

		try {
			input = buildCreateLimitPolicyInput(
				values,
				idempotentLimitPolicyId
			);
		} catch (error) {
			showMutationErrorToast('limit-policies', 'create', { error });
			return;
		}

		try {
			const { data } = await createLimitPolicy(
				getMutationOptions('limit-policies', { input })
			);

			const message = extractCheckResultError(data?.createLimitPolicy);

			if (message) {
				showMutationErrorToast('limit-policies', 'create', { message });
				return;
			}

			showMutationSuccessToast('limit-policies', 'create');
			go({
				to: '/limit-policies',
				state: { selectLimitPolicyId: idempotentLimitPolicyId }
			});
		} catch (error) {
			showMutationErrorToast('limit-policies', 'create', { error });
		}
	};

	return (
		<FormPageLayout>
			{lookupError && !shouldShowLoading && (
				<Alert variant="destructive">
					<AlertTitle>Unable to load form data</AlertTitle>
					<AlertDescription>
						{lookupError.message ??
							'Some lookup values could not be loaded.'}
					</AlertDescription>
				</Alert>
			)}

			{shouldShowLoading ? (
				<Card>
					<div className="text-sm text-muted-foreground">
						Loading form data...
					</div>
				</Card>
			) : (
				<LimitPolicyForm
					mode={cloneData ? 'clone' : 'create'}
					form={form}
					billingCycles={billingCycles}
					existingPriorities={existingPriorities}
					onSubmit={handleSubmit}
					isSubmitting={isSubmitting}
					initialScenarioLabel={cloneData?.scenarioLabel ?? undefined}
					initialLimitLabel={cloneData?.limitLabel ?? undefined}
					headerTitle={cloneData ? 'Clone Limit Policy' : 'Create Limit Policy'}
				/>
			)}
		</FormPageLayout>
	);
};
